import { useCallback, useState, useEffect, useRef } from 'react';
import {
  DocumentChartBarIcon, ArrowDownTrayIcon,
  UserGroupIcon, GlobeAltIcon, DevicePhoneMobileIcon,
  ClockIcon, ChartBarIcon, PresentationChartLineIcon,
  MagnifyingGlassCircleIcon, CursorArrowRaysIcon, EyeIcon
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { Card, Button, Select, Badge, Loading } from '../../components/Common';
import api from '../../services/api';
import { seoAnalyticsAPI, adAnalyticsAPI } from '../../services/api';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { useDispatch } from 'react-redux';

const COLORS = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

const Reports = () => {
  const dispatch = useDispatch();
  const reportRef = useRef(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  const [data, setData] = useState({
    overview: null, traffic: null, devices: null, geography: null, content: null,
    seo: null, ads: null
  });
  const [downloading, setDownloading] = useState(false);

  const tabs = [
    { key: 'overview', label: 'Overview', icon: ChartBarIcon },
    { key: 'traffic', label: 'Traffic Sources', icon: GlobeAltIcon },
    { key: 'devices', label: 'Devices', icon: DevicePhoneMobileIcon },
    { key: 'geography', label: 'Geography', icon: GlobeAltIcon },
    { key: 'content', label: 'Content', icon: DocumentChartBarIcon },
    { key: 'seo', label: 'SEO Analytics', icon: MagnifyingGlassCircleIcon },
    { key: 'ads', label: 'Ad Analytics', icon: CursorArrowRaysIcon },
  ];

  const dateRanges = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '365', label: 'Last year' },
  ];

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, devicesRes, geoRes, monthlyRes, contentRes, seoRes, seoPagesRes, adOverviewRes, adSizeRes, adBannerRes, adPositionRes, adDeviceRes, trafficRes, engagementRes] = await Promise.all([
        api.get('/admin/analytics/dashboard'),
        api.get('/admin/analytics/user-activity/devices').catch(() => null),
        api.get('/admin/analytics/user-activity/geography').catch(() => null),
        api.get('/admin/analytics/monthly'),
        api.get('/admin/analytics/content'),
        seoAnalyticsAPI.getOverview({ period: dateRange }),
        seoAnalyticsAPI.getPages({ period: dateRange }),
        adAnalyticsAPI.getOverview({ period: dateRange }),
        adAnalyticsAPI.getBySize(),
        adAnalyticsAPI.getByBanner(),
        adAnalyticsAPI.getByPosition(),
        adAnalyticsAPI.getByDevice().catch(() => ({ data: { data: { devicePerformance: [] } } })),
        api.get('/admin/analytics/user-activity/traffic-sources', { params: { period: dateRange } }).catch(() => null),
        api.get('/admin/analytics/user-activity/engagement', { params: { period: dateRange } }).catch(() => null),
      ]);

      const dashboardData = dashboardRes.data.data || {};
      const dashboard = dashboardData.overview || {};
      const rawMonthly = dashboardData.monthlyStats || monthlyRes.data.data || [];
      const monthly = rawMonthly.map(m => ({
        ...m,
        users: m.userCount || 0,
        articles: m.articleCount || 0,
      }));
      const content = contentRes.data.data || {};

      const seoOverview = seoRes.data?.data?.overview || {};
      const seoPages = seoPagesRes.data?.data?.topPages || [];
      const adOverview = adOverviewRes.data?.data?.overview || {};
      const adRecent = adOverviewRes.data?.data?.recentAds || [];
      const adSizeData = adSizeRes.data?.data || {};
      const adBannerData = adBannerRes.data?.data || {};
      const adPositionData = adPositionRes.data?.data || {};
      const adDeviceData = adDeviceRes.data?.data?.devicePerformance || [];

      const seoData = {
        overview: seoOverview,
        pages: seoPages,
      };

      const adData = {
        overview: adOverview,
        recent: adRecent,
        bySize: adSizeData.bySize || [],
        bestSize: adSizeData.bestPerformingSize || null,
        banners: adBannerData.banners || [],
        topBanners: adBannerData.topBanners || [],
        byPosition: adPositionData.byPosition || [],
        bestPosition: adPositionData.bestPosition || null,
        byDevice: adDeviceData,
      };

      const rawDevices = devicesRes?.data?.data;
      const deviceStatsList = rawDevices?.deviceStats || [];
      const deviceChartData = deviceStatsList.length > 0
        ? deviceStatsList.map(d => ({
            name: d.platform || 'Unknown',
            value: Number(d.count) || 0,
            color: COLORS[['iOS', 'Android', 'Web'].indexOf(d.platform) % COLORS.length] || '#94a3b8',
          }))
        : [];

      const rawGeo = geoRes?.data?.data;
      const geoCountries = rawGeo?.countries || [];
      const allCities = rawGeo?.cities || [];
      const geographyData = geoCountries.length > 0
        ? geoCountries.map(c => ({
            country: c.name,
            visitors: Number(c.count) || 0,
            cities: allCities.slice(0, 3).map(ct => ({ city: ct.name, count: Number(ct.count) || 0 })),
          }))
        : [];

      const trafficSources = trafficRes?.data?.data?.sources || [];

      const engagement = engagementRes?.data?.data || null;

      setData({
        overview: { dashboard, monthly, users: dashboardRes.data },
        traffic: trafficSources,
        devices: {
          chartData: deviceChartData,
          browsers: rawDevices?.browsers || [],
          operatingSystems: rawDevices?.operatingSystems || [],
        },
        geography: geographyData,
        content: { ...content, monthly, topArticles: content.topArticles || [] },
        seo: seoData,
        ads: adData,
        engagement,
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
      dispatch(showError('Failed to load analytics data'));
    } finally {
      setLoading(false);
    }
  }, [dateRange, dispatch]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const handleDownloadPDF = () => {
    setDownloading(true);
    try {
      const printWindow = window.open('', '_blank');
      const styles = Array.from(document.styleSheets)
        .map(sheet => {
          try {
            return Array.from(sheet.cssRules).map(rule => rule.cssText).join('');
          } catch { return ''; }
        }).join('');

      printWindow.document.write(`<!DOCTYPE html><html><head><title>Analytics Report</title>
        <style>${styles} body { padding: 40px; font-family: system-ui; }
        .chart-container, .no-print { display: none; }
        @media print { .no-print { display: none !important; } }
        .print-only { display: block !important; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
        th { background: #f8fafc; font-weight: 600; }
        .header { text-align: center; margin-bottom: 40px; }
        .header h1 { font-size: 28px; margin: 0; }
        .header p { color: #64748b; }
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
        .stat-card { background: #f8fafc; padding: 16px; border-radius: 8px; text-align: center; }
        .stat-card h3 { font-size: 24px; margin: 0; color: #1e293b; }
        .stat-card p { margin: 4px 0 0; color: #64748b; font-size: 14px; }
        h2 { margin: 32px 0 16px; color: #1e293b; }
        </style></head><body>
        <div class="header"><h1>Analytics Report</h1>
        <p>Generated: ${new Date().toLocaleDateString()} | Period: Last ${dateRange} days</p></div>`);

      const overview = data.overview?.dashboard;
      if (overview) {
        printWindow.document.write(`<h2>Overview</h2>
          <div class="stats-grid">
          <div class="stat-card"><h3>${overview.totalUsers || 0}</h3><p>Total Users</p></div>
          <div class="stat-card"><h3>${overview.totalArticles || 0}</h3><p>Articles</p></div>
          <div class="stat-card"><h3>${overview.totalCategories || 0}</h3><p>Categories</p></div>
          <div class="stat-card"><h3>${overview.totalContacts || 0}</h3><p>Contacts</p></div></div>`);
      }

      if (data.traffic) {
        printWindow.document.write(`<h2>Traffic Sources</h2><table><tr><th>Source</th><th>Share (%)</th></tr>
          ${data.traffic.map(t => `<tr><td>${t.name}</td><td>${t.value}%</td></tr>`).join('')}</table>`);
      }

      if (data.geography && data.geography.length > 0) {
        printWindow.document.write(`<h2>Top Countries</h2><table><tr><th>Country</th><th>Visitors</th></tr>
          ${data.geography.slice(0, 10).map(g => `<tr><td>${g.country}</td><td>${g.visitors?.toLocaleString() || 0}</td></tr>`).join('')}</table>`);
      }

      const seo = data.seo?.overview;
      if (seo) {
        printWindow.document.write(`<h2>SEO Analytics</h2>
          <div class="stats-grid">
          <div class="stat-card"><h3>${seo.totalIndexedPages || 0}</h3><p>Indexed Pages</p></div>
          <div class="stat-card"><h3>${seo.averageSeoScore || 'N/A'}</h3><p>Avg SEO Score</p></div>
          <div class="stat-card"><h3>${((seo.totalViews || 0)).toLocaleString()}</h3><p>Organic Traffic</p></div>
          <div class="stat-card"><h3>${seo.analyzedPages || 0}</h3><p>Analyzed Pages</p></div></div>`);
        const pages = data.seo?.pages || [];
        if (pages.length > 0) {
          printWindow.document.write(`<h2>Top Pages by SEO Score</h2><table><tr><th>Page</th><th>SEO Score</th><th>Readability</th><th>Words</th></tr>
            ${pages.slice(0, 10).map(p => `<tr><td>${p.title}</td><td>${p.seoScore || 'N/A'}</td><td>${p.readabilityScore || 'N/A'}</td><td>${p.wordCount?.toLocaleString() || 0}</td></tr>`).join('')}</table>`);
        }
      }

      const ads = data.ads?.overview;
      if (ads) {
        printWindow.document.write(`<h2>Advertisement Analytics</h2>
          <div class="stats-grid">
          <div class="stat-card"><h3>${ads.totalAds || 0}</h3><p>Total Ads</p></div>
          <div class="stat-card"><h3>${ads.activeAds || 0}</h3><p>Active Ads</p></div>
          <div class="stat-card"><h3>${(ads.totalImpressions || 0).toLocaleString()}</h3><p>Impressions</p></div>
          <div class="stat-card"><h3>${(ads.totalClicks || 0).toLocaleString()}</h3><p>Clicks</p></div></div>`);
        const banners = data.ads?.banners || [];
        if (banners.length > 0) {
          printWindow.document.write(`<h2>Banner Performance</h2><table><tr><th>Banner</th><th>Slot</th><th>Impressions</th><th>Clicks</th><th>CTR</th></tr>
            ${banners.slice(0, 10).map(b => `<tr><td>${b.name}</td><td>${b.slot}</td><td>${b.impressions.toLocaleString()}</td><td>${b.clicks.toLocaleString()}</td><td>${b.ctr.toFixed(2)}%</td></tr>`).join('')}</table>`);
        }
      }

      printWindow.document.write(`</body></html>`);
      printWindow.document.close();
      setTimeout(() => { printWindow.print(); }, 500);
      dispatch(showSuccess('Report opened for printing/saving as PDF'));
    } catch (error) {
      console.error('PDF error:', error);
      dispatch(showError('Failed to generate PDF'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const { startDate, endDate } = getDateRange(parseInt(dateRange));
      const response = await api.get('/admin/reports/system', {
        params: { startDate, endDate, format: 'csv' },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(showSuccess('Report downloaded successfully'));
    } catch (error) {
      dispatch(showError('Failed to download report'));
    }
  };

  const getDateRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
  };

  const renderOverview = () => {
    const d = data.overview?.dashboard || {};
    const monthly = data.overview?.monthly || [];
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: d.totalUsers || 0, icon: UserGroupIcon, color: 'from-indigo-500 to-purple-500' },
            { label: 'Total Articles', value: d.totalArticles || 0, icon: DocumentChartBarIcon, color: 'from-emerald-500 to-teal-500' },
            { label: 'Categories', value: d.totalCategories || 0, icon: ChartBarIcon, color: 'from-amber-500 to-orange-500' },
            { label: 'Total Contacts', value: d.totalContacts || 0, icon: GlobeAltIcon, color: 'from-pink-500 to-rose-500' },
          ].map((item, i) => (
            <div key={i} className={`bg-gradient-to-r ${item.color} text-white rounded-xl p-5 shadow-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">{item.label}</p>
                  <p className="text-3xl font-bold">{item.value}</p>
                </div>
                <item.icon className="w-12 h-12 text-white/30" />
              </div>
            </div>
          ))}
        </div>

        {monthly.length > 0 && (
          <Card title="Monthly Trends" subtitle="User registrations and content over time">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="users" stroke="#6366f1" strokeWidth={2} name="Users" />
                <Line type="monotone" dataKey="articles" stroke="#14b8a6" strokeWidth={2} name="Articles" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {(!monthly || monthly.length === 0) && (
          <Card title="Monthly Trends" subtitle="No data available yet">
            <div className="flex items-center justify-center h-64 text-slate-400">No monthly data available</div>
          </Card>
        )}
      </div>
    );
  };

  const renderTraffic = () => {
    const traffic = data.traffic || [];
    const engagement = data.engagement || {};

    return (
      <div className="space-y-6">
        {traffic.length > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {traffic.map((t, i) => (
                <Card key={i}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.color + '20' }}>
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t.name}</p>
                      <p className="text-xl font-bold">{t.value}%</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title="Traffic Distribution">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={traffic} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {traffic.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>

              <Card title="Traffic by Source">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={traffic}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" name="Share (%)">
                      {traffic.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          </>
        )}

        {traffic.length === 0 && (
          <Card title="Traffic Sources" subtitle="No traffic data available">
            <div className="flex items-center justify-center h-64 text-slate-400">
              No traffic source data available yet. Data will appear once articles receive views.
            </div>
          </Card>
        )}

        {engagement && (
          <Card title="Engagement Metrics" subtitle="Key visitor engagement indicators">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <p className="text-sm text-slate-500">Avg Session Duration</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{engagement.avgSessionDuration || '0s'}</p>
              </Card>
              <Card>
                <p className="text-sm text-slate-500">Bounce Rate</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{engagement.bounceRate || '0%'}</p>
              </Card>
              <Card>
                <p className="text-sm text-slate-500">Pages/Session</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{engagement.pagesPerSession || '0'}</p>
              </Card>
              <Card>
                <p className="text-sm text-slate-500">Exit Rate</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{engagement.exitRate || '0%'}</p>
              </Card>
            </div>
          </Card>
        )}
      </div>
    );
  };

  const renderDevices = () => {
    const rawDevices = data.devices || {};
    const chartData = rawDevices.chartData || [];
    const browsers = rawDevices.browsers || [];
    const operatingSystems = rawDevices.operatingSystems || [];

    const browserList = browsers.length > 0
      ? browsers.map(b => ({ name: b.name, share: b.percentage, color: COLORS[['Chrome', 'Firefox', 'Safari', 'Edge'].indexOf(b.name) % COLORS.length] || '#94a3b8' }))
      : [];

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Device Breakdown">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {chartData.map((entry, i) => <Cell key={i} fill={entry.color || COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-500 text-sm py-8 text-center">No device data available</p>
            )}
          </Card>

          <Card title="Browser Analytics">
            {browserList.length > 0 ? (
              <div className="space-y-4">
                {browserList.map((b, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-700">{b.name}</span>
                      <span className="font-medium">{b.share}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="h-2 rounded-full transition-all" style={{ width: `${b.share}%`, backgroundColor: b.color }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm py-8 text-center">No browser data available</p>
            )}
          </Card>
        </div>

        <Card title="Operating Systems">
          {operatingSystems.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {operatingSystems.map((os, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-800">{os.percentage}%</p>
                  <p className="text-sm text-slate-500 mt-1">{os.name}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm py-4 text-center">No operating system data available</p>
          )}
        </Card>
      </div>
    );
  };

  const renderGeography = () => {
    const geo = data.geography || [];
    if (geo.length === 0) {
      return (
        <Card title="Geography" subtitle="No geographic data available">
          <div className="flex items-center justify-center h-64 text-slate-400">
            No visitor geographic data available yet.
          </div>
        </Card>
      );
    }
    return (
      <div className="space-y-6">
        <Card title="Top Countries by Visitors" noPadding>
          <div className="divide-y divide-slate-100">
            {geo.slice(0, 10).map((g, i) => {
              const maxVisitors = Math.max(...geo.map(x => x.visitors || 0));
              const pct = maxVisitors > 0 ? ((g.visitors || 0) / maxVisitors) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-4 p-4">
                  <span className="text-sm font-medium text-slate-400 w-6">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium text-slate-800">{g.country}</span>
                      <span className="text-sm text-slate-500">{(g.visitors || 0).toLocaleString()} visitors</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card title="City-wise Visitors">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {geo.slice(0, 6).map((g, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-4">
                <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <GlobeAltIcon className="w-4 h-4 text-indigo-500" />
                  {g.country}
                </h4>
                <div className="space-y-2">
                  {(g.cities || []).slice(0, 3).map((c, j) => (
                    <div key={j} className="flex justify-between text-sm">
                      <span className="text-slate-600">{c.city}</span>
                      <span className="font-medium">{c.count?.toLocaleString() || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  const renderContent = () => {
    const content = data.content || {};
    const topArticles = content.topArticles || [];
    const topCategories = content.topCategories || [];
    const articleStats = content.articleStats || [];
    const overview = data.overview?.dashboard || {};
    const totalViews = data.seo?.overview?.totalViews || 0;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <p className="text-sm text-slate-500">Total Articles</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{overview.totalArticles || 0}</p>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Total Views</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{(totalViews || 0).toLocaleString()}</p>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Categories</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{overview.totalCategories || 0}</p>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Top Categories</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{topCategories.length}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Recent Articles" noPadding>
            {topArticles.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {topArticles.slice(0, 8).map((a, i) => (
                  <div key={a.id || i} className="flex items-center justify-between py-3 px-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-800 truncate">{a.title || 'Untitled'}</p>
                      <p className="text-sm text-slate-500">{a.category?.name || 'Uncategorized'}</p>
                    </div>
                    <p className="text-xs text-slate-500 ml-2">{a.created_on ? new Date(a.created_on).toLocaleDateString() : ''}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-slate-400">No articles found</div>
            )}
          </Card>

          <Card title="Top Categories" noPadding>
            {topCategories.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {topCategories.map((c, i) => (
                  <div key={c.id || i} className="flex items-center justify-between py-3 px-4">
                    <span className="font-medium text-slate-800">{c.name}</span>
                    <span className="text-sm text-slate-600">{c.articleCount} articles</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-slate-400">No categories found</div>
            )}
          </Card>
        </div>

        {articleStats.length > 0 && (
          <Card title="Daily Article Creation" subtitle="Articles created per day">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={articleStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#6366f1" fill="#6366f1" fillOpacity={0.1} strokeWidth={2} name="Articles" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    );
  };

  const renderSEOAnalytics = () => {
    const seo = data.seo || {};
    const overview = seo.overview || {};
    const pages = seo.pages || [];

    const avgScore = overview.averageSeoScore;
    const totalViews = overview.totalViews || 0;
    const viewsChange = overview.viewsChange;

    const seoScoreBg = avgScore >= 80 ? 'from-emerald-500 to-teal-500' : avgScore >= 60 ? 'from-amber-500 to-orange-500' : 'from-red-500 to-pink-500';

    const organicImpressions = totalViews;
    const organicClicks = Math.round(organicImpressions * 0.036);
    const organicCTR = organicImpressions > 0 ? ((organicClicks / organicImpressions) * 100).toFixed(2) : '0.00';

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`bg-gradient-to-r ${seoScoreBg} text-white rounded-xl p-5 shadow-lg`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Avg SEO Score</p>
                <p className="text-3xl font-bold">{avgScore !== null ? `${avgScore}/100` : 'N/A'}</p>
              </div>
              <PresentationChartLineIcon className="w-12 h-12 text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Indexed Pages</p>
                <p className="text-3xl font-bold">{overview.totalIndexedPages || 0}</p>
              </div>
              <DocumentChartBarIcon className="w-12 h-12 text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Organic Traffic</p>
                <p className="text-3xl font-bold">{(organicImpressions || 0).toLocaleString()}</p>
              </div>
              <GlobeAltIcon className="w-12 h-12 text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Content Growth</p>
                <p className="text-3xl font-bold">{overview.contentGrowth !== null ? `${overview.contentGrowth}%` : 'N/A'}</p>
              </div>
              <ChartBarIcon className="w-12 h-12 text-white/30" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <p className="text-sm text-slate-500">Search Clicks</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{organicClicks.toLocaleString()}</p>
            <Badge variant={organicClicks > 0 ? 'success' : 'default'}>Estimate</Badge>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Impressions</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{(organicImpressions || 0).toLocaleString()}</p>
            <Badge variant={organicImpressions > 0 ? 'success' : 'default'}>Organic</Badge>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Avg CTR</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{organicCTR}%</p>
            <Badge variant={parseFloat(organicCTR) > 3 ? 'success' : 'warning'}>Estimate</Badge>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Analyzed Pages</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{overview.analyzedPages || 0}</p>
            <Badge variant={overview.analyzedPages > 0 ? 'success' : 'default'}>{overview.totalIndexedPages > 0 ? `${Math.round(((overview.analyzedPages || 0) / overview.totalIndexedPages) * 100)}%` : '0%'}</Badge>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Views Change</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{viewsChange !== null ? `${viewsChange >= 0 ? '+' : ''}${viewsChange}%` : 'N/A'}</p>
            <Badge variant={viewsChange >= 0 ? 'success' : 'danger'}>{viewsChange >= 0 ? 'Up' : 'Down'}</Badge>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Top Ranking Pages" subtitle="SEO scores across articles" noPadding>
            {pages.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {pages.slice(0, 10).map((p, i) => (
                  <div key={p.id} className="flex items-center gap-4 p-3">
                    <span className="text-sm font-medium text-slate-400 w-6">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.title}</p>
                      <p className="text-xs text-slate-500 truncate">/{p.url}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`px-2 py-0.5 rounded font-medium ${p.seoScore >= 80 ? 'bg-emerald-100 text-emerald-700' : p.seoScore >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        SEO: {p.seoScore || 'N/A'}
                      </span>
                      <span className="text-slate-500">{p.wordCount ? `${p.wordCount.toLocaleString()}w` : '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm p-4">No SEO analysis data yet. Analyze articles to see scores.</p>
            )}
          </Card>
          <Card title="Core Web Vitals & Performance" subtitle="PageSpeed Insights metrics">
            <div className="space-y-4">
              {[
                { label: 'Page Load Speed', status: 'pending', desc: 'Requires PageSpeed Insights API' },
                { label: 'Mobile Performance', status: 'pending', desc: 'Configure API key in settings' },
                { label: 'Desktop Performance', status: 'pending', desc: 'Configure API key in settings' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-3 border border-slate-200 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <ClockIcon className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{item.label}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                  <Badge variant="warning">Pending Setup</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {pages.length > 0 && (
          <Card title="SEO Score Distribution" subtitle="Readability vs SEO Score">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pages.filter(p => p.seoScore !== null).slice(0, 20)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="title" tick={false} />
                <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.title || ''} />
                <Legend />
                <Bar dataKey="seoScore" fill="#6366f1" name="SEO Score" radius={[4, 4, 0, 0]} />
                <Bar dataKey="readabilityScore" fill="#14b8a6" name="Readability" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        <Card title="Organic Traffic Metrics" subtitle="Impressions, clicks and CTR estimates">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={[
              { metric: 'Impressions', value: organicImpressions || 0, fill: '#6366f1' },
              { metric: 'Estimated Clicks', value: organicClicks || 0, fill: '#14b8a6' },
              { metric: 'CTR %', value: parseFloat(organicCTR) * 100 || 0, fill: '#f59e0b' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="metric" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.1} strokeWidth={2} name="Value" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>
    );
  };

  const renderAdAnalytics = () => {
    const ads = data.ads || {};
    const overview = ads.overview || {};
    const bySize = ads.bySize || [];
    const banners = ads.banners || [];
    const byPosition = ads.byPosition || [];
    const bestSize = ads.bestSize;
    const bestPosition = ads.bestPosition;

    const totalImpressions = overview.totalImpressions || 0;
    const totalClicks = overview.totalClicks || 0;
    const overallCTR = overview.overallCTR || 0;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Total Ads</p>
                <p className="text-3xl font-bold">{overview.totalAds || 0}</p>
              </div>
              <DocumentChartBarIcon className="w-12 h-12 text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Active Ads</p>
                <p className="text-3xl font-bold">{overview.activeAds || 0}</p>
              </div>
              <ChartBarIcon className="w-12 h-12 text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Total Impressions</p>
                <p className="text-3xl font-bold">{totalImpressions.toLocaleString()}</p>
              </div>
              <EyeIcon className="w-12 h-12 text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl p-5 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Total Clicks</p>
                <p className="text-3xl font-bold">{totalClicks.toLocaleString()}</p>
              </div>
              <CursorArrowRaysIcon className="w-12 h-12 text-white/30" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <p className="text-sm text-slate-500">Click Through Rate</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{overallCTR.toFixed(2)}%</p>
            <Badge variant={overview.ctrChange >= 0 ? 'success' : overview.ctrChange < 0 ? 'danger' : 'default'}>
              {overview.ctrChange !== null ? `${overview.ctrChange >= 0 ? '+' : ''}${overview.ctrChange}%` : 'Baseline'}
            </Badge>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Inactive Ads</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{overview.inactiveAds || 0}</p>
            <Badge variant={overview.inactiveAds > 0 ? 'warning' : 'success'}>{overview.inactiveAds > 0 ? 'Needs Attention' : 'All Active'}</Badge>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Impressions Change</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{overview.impressionsChange !== null ? `${overview.impressionsChange >= 0 ? '+' : ''}${overview.impressionsChange}%` : 'N/A'}</p>
            <Badge variant={overview.impressionsChange >= 0 ? 'success' : 'danger'}>{overview.impressionsChange >= 0 ? 'Growing' : 'Declining'}</Badge>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Best Performing Size</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{bestSize ? `${bestSize.width}x${bestSize.height}` : 'N/A'}</p>
            <p className="text-xs text-slate-500 mt-1">{bestSize ? `${bestSize.sizeName} (${bestSize.ctr.toFixed(2)}% CTR)` : 'No data'}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Ad Size Performance" subtitle="CTR by ad format size">
            {bySize.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={bySize}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="sizeName" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalImpressions" fill="#6366f1" name="Impressions" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="totalClicks" fill="#14b8a6" name="Clicks" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-500 text-sm">No ad size data available. Ad impressions will populate this chart.</p>
            )}
          </Card>

          <Card title="Ad Position Performance" subtitle="CTR by placement">
            {byPosition.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {byPosition.map((pos, i) => (
                  <div key={i} className="flex items-center gap-4 p-3">
                    <span className="text-sm font-medium text-slate-400 w-6">{i + 1}</span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{pos.label}</p>
                      <p className="text-xs text-slate-500">{pos.adCount} ads</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-800">{pos.ctr.toFixed(2)}% CTR</p>
                      <p className="text-xs text-slate-500">{pos.totalImpressions.toLocaleString()} impressions</p>
                    </div>
                    <div className="w-24 bg-slate-100 rounded-full h-2">
                      <div className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${Math.min(pos.ctr * 10, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm p-4">No position data yet. Ad impressions will appear here.</p>
            )}
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Banner-wise Performance" subtitle="Individual ad performance" noPadding>
            {banners.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {banners.slice(0, 10).map((b, i) => (
                  <div key={b.id} className="flex items-center gap-4 p-3">
                    <span className="text-sm font-medium text-slate-400 w-6">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{b.name}</p>
                      <p className="text-xs text-slate-500">{b.slot}</p>
                    </div>
                    <div className="text-right text-xs">
                      <p className="font-medium">{b.impressions.toLocaleString()} imp</p>
                      <p className="text-slate-500">{b.clicks.toLocaleString()} clk</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${b.ctr >= 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {b.ctr.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm p-4">No banner data yet. Ad impressions will populate this list.</p>
            )}
          </Card>

          <Card title="Device-wise Ad Performance" subtitle="Performance across devices">
            <div className="space-y-4">
              {ads.byDevice && ads.byDevice.length > 0 ? (
                ads.byDevice.map((d, i) => {
                  const deviceColors = {
                    Desktop: 'bg-indigo-100 text-indigo-600',
                    Mobile: 'bg-emerald-100 text-emerald-600',
                    Tablet: 'bg-amber-100 text-amber-600',
                  };
                  const color = deviceColors[d.device] || 'bg-slate-100 text-slate-600';
                  return (
                    <div key={i} className="flex items-center gap-4 p-3 border border-slate-200 rounded-lg">
                      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
                        <DevicePhoneMobileIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{d.device}</p>
                        <p className="text-sm text-slate-500">
                          {d.impressions.toLocaleString()} impressions · {d.clicks.toLocaleString()} clicks
                        </p>
                      </div>
                      <Badge variant={d.ctr > 0 ? 'success' : 'default'}>{d.ctr.toFixed(2)}% CTR</Badge>
                    </div>
                  );
                })
              ) : (
                <div className="space-y-4">
                  {[
                    { device: 'Desktop', icon: DevicePhoneMobileIcon, color: 'bg-indigo-100 text-indigo-600' },
                    { device: 'Mobile', icon: DevicePhoneMobileIcon, color: 'bg-emerald-100 text-emerald-600' },
                    { device: 'Tablet', icon: DevicePhoneMobileIcon, color: 'bg-amber-100 text-amber-600' },
                  ].map((d, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 border border-slate-200 rounded-lg">
                      <div className={`w-10 h-10 rounded-lg ${d.color} flex items-center justify-center flex-shrink-0`}>
                        <d.icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{d.device}</p>
                        <p className="text-sm text-slate-500">Requires analytics service integration</p>
                      </div>
                      <Badge variant="warning">Pending</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        <Card title="CTR Trend" subtitle="Click-through rate comparison">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={[
              { name: 'Overall CTR', value: overallCTR, fill: '#6366f1' },
              { name: 'Best Size CTR', value: bestSize?.ctr || 0, fill: '#14b8a6' },
              { name: 'Best Position CTR', value: bestPosition?.ctr || 0, fill: '#f59e0b' },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="%" />
              <Tooltip formatter={(val) => `${Number(val).toFixed(2)}%`} />
              <Bar dataKey="value" name="CTR (%)" radius={[4, 4, 0, 0]}>
                {[{ name: 'Overall CTR', value: overallCTR, fill: '#6366f1' },
                  { name: 'Best Size CTR', value: bestSize?.ctr || 0, fill: '#14b8a6' },
                  { name: 'Best Position CTR', value: bestPosition?.ctr || 0, fill: '#f59e0b' },
                ].map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    );
  };

  const renderTabContent = () => {
    if (loading) return <Loading fullScreen text="Loading analytics..." />;
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'traffic': return renderTraffic();
      case 'devices': return renderDevices();
      case 'geography': return renderGeography();
      case 'content': return renderContent();
      case 'seo': return renderSEOAnalytics();
      case 'ads': return renderAdAnalytics();
      default: return renderOverview();
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" ref={reportRef}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Analytics Reports</h1>
          <p className="text-slate-500">Comprehensive website performance and visitor insights</p>
        </div>
        <div className="flex gap-2 no-print">
          <Button variant="secondary" icon={ArrowDownTrayIcon} onClick={handleDownloadCSV}>
            Export CSV
          </Button>
          <Button icon={PresentationChartLineIcon} onClick={handleDownloadPDF} loading={downloading}>
            {downloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1 w-full">
            <div className="flex border-b border-slate-200">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            options={dateRanges}
            containerClassName="w-40"
          />
        </div>
      </Card>

      {renderTabContent()}
    </div>
  );
};

export default Reports;
